/* ==========================================================================
   Studio2Seven — theme.js
   Plain JavaScript, no libraries, no build step.
   Sections:
     1. Helpers            7. Product page (sizes, gallery, zoom, lightbox, add to bag)
     2. Header + menu      8. Wishlist
     3. Announcement bar   9. Bag (cart) page
     4. Scroll reveal     10. Collection (sort, load more, infinite scroll)
     5. Carousels         11. Recommendations + small extras
     6. Dialogs / modals
   Settings from the theme editor are read from window.S27 (layout/theme.liquid).
   ========================================================================== */
(function () {
  'use strict';

  var S27 = window.S27 || {};
  var strings = S27.strings || {};
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Helpers ---------- */
  function $(selector, scope) { return (scope || document).querySelector(selector); }
  function $$(selector, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(selector)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Shopify money format, e.g. "₹{{amount}}" — used only for wishlist cards */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || S27.moneyFormat || '{{amount}}';
    function delimit(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return delimit(cents, 2, ',', '.');
        case 'amount_no_decimals': return delimit(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        default: return delimit(cents, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  }

  /* Small status message at the bottom of the screen */
  var toastTimer;
  function showToast(message, linkText, linkUrl) {
    var toast = $('#Toast');
    if (!toast) return;
    toast.innerHTML = '<span>' + escapeHtml(message) + '</span>' +
      (linkText ? '<a href="' + escapeHtml(linkUrl) + '">' + escapeHtml(linkText) + '</a>' : '');
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.hidden = true; }, 4000);
  }


  /* ---------- 2. Header + menu ---------- */
  function initHeader() {
    var wrapper = $('[data-header-wrapper]');
    if (!wrapper) return;

    // Expose the header height to CSS (used by sticky elements and the hero)
    function setHeight() {
      document.documentElement.style.setProperty('--header-height', wrapper.offsetHeight + 'px');
    }
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));

    // Solid background once the page is scrolled
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        wrapper.classList.toggle('is-scrolled', window.scrollY > 10);
        ticking = false;
      });
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Desktop dropdowns open on hover (and still work with click/keyboard)
    $$('[data-hover-details]').forEach(function (details) {
      var item = details.parentElement;
      var hoverCapable = window.matchMedia('(hover: hover)').matches;
      if (hoverCapable) {
        item.addEventListener('mouseenter', function () { details.setAttribute('open', ''); });
        item.addEventListener('mouseleave', function () { details.removeAttribute('open'); });
      }
      details.addEventListener('focusout', function (e) {
        if (!details.contains(e.relatedTarget)) details.removeAttribute('open');
      });
    });

    // Search panel: focus the field when opened
    var search = $('[data-search-details]');
    if (search) {
      search.addEventListener('toggle', function () {
        if (search.open) { var input = $('input[type="search"]', search); if (input) input.focus(); }
      });
    }

    // Close open header panels on Escape or outside click
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      $$('.header details[open]').forEach(function (d) { d.removeAttribute('open'); var s = $('summary', d); if (s) s.focus(); });
    });
    document.addEventListener('click', function (e) {
      $$('.header details[open]').forEach(function (d) { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
  }

  function initDrawer() {
    $$('[data-drawer-open]').forEach(function (btn) {
      var drawer = document.getElementById(btn.getAttribute('data-drawer-open'));
      if (!drawer) return;
      var panel = $('.drawer__panel', drawer);

      function open() {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('is-locked');
        var first = $('a, button', panel);
        setTimeout(function () { if (first) first.focus(); }, 50);
      }
      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        btn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('is-locked');
        btn.focus();
      }

      btn.addEventListener('click', open);
      $$('[data-drawer-close]', drawer).forEach(function (el) { el.addEventListener('click', close); });
      drawer.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
        // Keep keyboard focus inside the open menu
        if (e.key === 'Tab') {
          var focusable = $$('a[href], button, summary', panel).filter(function (el) { return el.offsetParent !== null; });
          if (!focusable.length) return;
          var first = focusable[0], last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      window.addEventListener('resize', debounce(function () {
        if (window.innerWidth >= 1200 && drawer.classList.contains('is-open')) close();
      }, 200));
    });
  }

  /* ---------- 3. Announcement bar ---------- */
  function initAnnouncement() {
    var bar = $('[data-announcement]');
    if (!bar) return;
    var items = $$('.announcement__item', bar);
    if (items.length < 2 || prefersReducedMotion) return;
    var index = 0, paused = false;
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    setInterval(function () {
      if (paused) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, 5000);
  }

  /* ---------- 4. Scroll reveal ---------- */
  var revealObserver = null;
  function initReveal(scope) {
    if (S27.animations === false || prefersReducedMotion || !('IntersectionObserver' in window)) {
      document.documentElement.classList.add('no-reveal');
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    $$('.reveal:not(.is-visible)', scope).forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------- 5. Carousels (bestsellers, lookbook, related) ---------- */
  function initCarousels(scope) {
    $$('[data-carousel-section]', scope).forEach(function (section) {
      if (section.dataset.carouselReady) return;
      section.dataset.carouselReady = 'true';
      var track = $('[data-carousel-track]', section);
      if (!track) return;
      function step(dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }
      var prev = $('[data-carousel-prev]', section);
      var next = $('[data-carousel-next]', section);
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      // Drag to scroll with a mouse (touch already swipes natively)
      var down = false, startX = 0, startLeft = 0, moved = false;
      track.addEventListener('mousedown', function (e) {
        down = true; moved = false; startX = e.pageX; startLeft = track.scrollLeft;
      });
      window.addEventListener('mousemove', function (e) {
        if (!down) return;
        var dx = e.pageX - startX;
        if (Math.abs(dx) > 5) { moved = true; track.classList.add('is-dragging'); }
        track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('mouseup', function () {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
      });
      // Don't follow a link at the end of a drag
      track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    });
  }

  /* ---------- 5b. Lookbook videos ----------
     Videos play silently on a loop only while visible (saves data and battery),
     and pause when scrolled away. They never play sound. Visitors who prefer
     reduced motion see the still poster image instead. */
  var videoObserver = null;
  var blockedVideos = [];
  var retryArmed = false;

  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  // Some browsers (e.g. iPhone Low Power Mode) block autoplay until the
  // visitor interacts — retry on the first scroll, tap or key press.
  function armRetry() {
    if (retryArmed) return;
    retryArmed = true;
    var events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
    function retry() {
      events.forEach(function (ev) { window.removeEventListener(ev, retry, true); });
      retryArmed = false;
      var waiting = blockedVideos.slice();
      blockedVideos = [];
      waiting.forEach(function (v) { if (inView(v)) playVideo(v); });
    }
    events.forEach(function (ev) { window.addEventListener(ev, retry, { capture: true, passive: true }); });
  }

  function playVideo(v) {
    var p = v.play();
    if (p && p.catch) {
      p.catch(function () {
        if (blockedVideos.indexOf(v) === -1) blockedVideos.push(v);
        armRetry();
      });
    }
  }

  function initVideos(scope) {
    var cards = $$('[data-video-card]', scope);
    if (!cards.length) return;
    cards.forEach(function (card) {
      var video = $('video', card);
      if (!video || video.dataset.ready) return;
      video.dataset.ready = 'true';
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
      // Always silent: keep muted even if the browser tries to unmute
      video.addEventListener('volumechange', function () { if (!video.muted) video.muted = true; });

      if (prefersReducedMotion) return;
      if (!('IntersectionObserver' in window)) { playVideo(video); return; }
      if (!videoObserver) {
        videoObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            if (entry.isIntersecting) {
              playVideo(v);
            } else {
              v.pause();
            }
          });
        }, { threshold: 0.35 });
      }
      videoObserver.observe(video);
    });
  }

  /* ---------- 6. Dialogs / modals ---------- */
  function initModals() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-modal-open]');
      if (opener) {
        var dialog = document.getElementById(opener.getAttribute('data-modal-open'));
        if (dialog && dialog.showModal) { dialog.showModal(); document.body.classList.add('is-locked'); }
        return;
      }
      var closer = e.target.closest('[data-modal-close]');
      if (closer) { var d = closer.closest('dialog'); if (d) d.close(); return; }
      // Click on the dimmed backdrop closes the dialog
      if (e.target.tagName === 'DIALOG' && e.target.classList.contains('modal')) e.target.close();
    });
    $$('dialog').forEach(function (d) {
      d.addEventListener('close', function () { document.body.classList.remove('is-locked'); });
    });
  }

  /* ---------- 7. Product page ---------- */
  function initProduct() {
    var root = $('[data-product]');
    if (!root) return;

    var form = $('[data-product-form]', root);
    var variantsEl = $('[data-variants-json]', root);
    var variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    var idInput = $('[data-variant-id]', root);
    var addButton = $('[data-add-to-bag]', root);
    var addLabel = $('[data-add-label]', root);
    var errorBox = $('[data-form-error]', root);
    var customNote = $('[data-custom-note]', root);
    var stickyAdd = $('[data-sticky-add]');
    var fieldsets = $$('.option-picker', root);

    var current = variants.filter(function (v) { return String(v.id) === (idInput && idInput.value); })[0] || variants[0];

    function selectedOptions() {
      return fieldsets.map(function (fs) {
        var checked = $('input:checked', fs);
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return variants.filter(function (v) {
        return v.options.every(function (opt, i) { return opt === options[i]; });
      })[0];
    }

    // Grey out sizes that are sold out given the other choices
    function markAvailability() {
      var selected = selectedOptions();
      fieldsets.forEach(function (fs, index) {
        $$('input', fs).forEach(function (input) {
          var test = selected.slice();
          test[index] = input.value;
          var match = findVariant(test);
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function update(variant) {
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (stickyAdd) stickyAdd.disabled = true;
        if (addLabel) addLabel.textContent = strings.unavailable || 'Unavailable';
        return;
      }
      current = variant;
      if (idInput) idInput.value = variant.id;

      // Price
      $$('[data-product-price] [data-price]', root).forEach(function (el) {
        el.classList.toggle('price--sale', !!variant.compare);
        el.innerHTML = '<span class="price__current">' + escapeHtml(variant.price) + '</span>' +
          (variant.compare ? '<s class="price__compare">' + escapeHtml(variant.compare) + '</s>' : '');
      });
      var stickyPrice = $('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.price;

      // Add to bag state
      if (addButton) addButton.disabled = !variant.available;
      if (stickyAdd) stickyAdd.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? strings.addToBag : strings.soldOut;

      // Selected value labels
      fieldsets.forEach(function (fs) {
        var checked = $('input:checked', fs);
        var label = $('[data-option-selected]', fs);
        if (checked && label) label.textContent = checked.value;
      });

      // "Custom / Made to measure" note
      if (customNote) customNote.hidden = !/custom/i.test(variant.title);

      // Show the photo linked to this variant
      if (variant.media_id) {
        var slide = $('[data-media-id="' + variant.media_id + '"]', root);
        if (slide && gallery) gallery.goTo(parseInt(slide.getAttribute('data-gallery-slide'), 10));
      }

      // Shareable URL for the chosen size
      if (window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', u.toString());
      }
      markAvailability();
    }

    root.addEventListener('change', function (e) {
      if (!e.target.matches('[data-option-input]')) return;
      update(findVariant(selectedOptions()));
    });

    // Add to bag without leaving the page
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!addButton || addButton.disabled) return;
        addButton.setAttribute('aria-busy', 'true');
        if (errorBox) errorBox.hidden = true;
        fetch((S27.routes.cartAdd || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
          .then(function (result) {
            if (!result.ok) throw new Error(result.data.description || result.data.message || strings.error);
            showToast(strings.added, strings.viewBag, S27.routes.cart);
            refreshCartCount();
          })
          .catch(function (err) {
            if (errorBox) { errorBox.textContent = err.message || strings.error; errorBox.hidden = false; }
          })
          .finally(function () { addButton.removeAttribute('aria-busy'); });
      });
    }

    // Sticky "Add to bag" bar on phones once the main buttons scroll out of view
    var sticky = $('[data-sticky-buy]');
    var actions = $('.product__actions', root);
    if (sticky && actions && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var out = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', out);
        sticky.setAttribute('aria-hidden', out ? 'false' : 'true');
        var stickyButton = $('button', sticky);
        if (stickyButton) stickyButton.tabIndex = out ? 0 : -1;
      }).observe(actions);
    }

    var gallery = initGallery(root);
    markAvailability();
  }

  /* Gallery: swipe (touch), thumbnails + hover zoom (desktop), full-screen lightbox */
  function initGallery(root) {
    var wrap = $('[data-gallery]', root);
    if (!wrap) return null;
    var track = $('[data-gallery-track]', wrap);
    var slides = $$('[data-gallery-slide]', wrap);
    var thumbs = $$('[data-gallery-thumb]', wrap);
    var dots = $$('.gallery__dot', wrap);
    var counter = $('[data-gallery-current]', wrap);
    var index = 0;

    function setActive(i) {
      index = i;
      thumbs.forEach(function (t, n) {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      if (counter) counter.textContent = i + 1;
    }

    function goTo(i, instant) {
      if (!slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: instant || prefersReducedMotion ? 'auto' : 'smooth' });
      setActive(i);
    }

    track.addEventListener('scroll', debounce(function () {
      var i = Math.round(track.scrollLeft / track.clientWidth);
      if (i !== index) setActive(i);
    }, 60), { passive: true });

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { goTo(parseInt(t.getAttribute('data-gallery-thumb'), 10)); });
    });
    var prev = $('[data-gallery-prev]', wrap);
    var next = $('[data-gallery-next]', wrap);
    if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
    });

    // Hover zoom — desktop mouse only
    var fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    $$('[data-zoom]', wrap).forEach(function (zoom) {
      var img = $('img', zoom);
      if (!img) return;
      zoom.addEventListener('mouseenter', function () { if (fineHover.matches) zoom.classList.add('is-zooming'); });
      zoom.addEventListener('mousemove', function (e) {
        if (!zoom.classList.contains('is-zooming')) return;
        var r = zoom.getBoundingClientRect();
        img.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
      });
      zoom.addEventListener('mouseleave', function () { zoom.classList.remove('is-zooming'); });
    });

    // Lightbox
    var lightbox = $('[data-lightbox]');
    if (lightbox && lightbox.showModal) {
      var lbTrack = $('[data-lightbox-track]', lightbox);
      var lbSlides = $$('[data-lightbox-slide]', lightbox);
      var lbIndex = 0;
      function lbGo(i, instant) {
        lbIndex = Math.max(0, Math.min(lbSlides.length - 1, i));
        if (lbSlides[lbIndex]) lbTrack.scrollTo({ left: lbSlides[lbIndex].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      }
      $$('[data-lightbox-open]', wrap).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-lightbox-open');
          var pos = lbSlides.findIndex(function (s) { return s.getAttribute('data-lightbox-slide') === target; });
          lightbox.showModal();
          document.body.classList.add('is-locked');
          requestAnimationFrame(function () { lbGo(pos < 0 ? 0 : pos, true); });
        });
      });
      $('[data-lightbox-close]', lightbox).addEventListener('click', function () { lightbox.close(); });
      var lbPrev = $('[data-lightbox-prev]', lightbox);
      var lbNext = $('[data-lightbox-next]', lightbox);
      if (lbPrev) lbPrev.addEventListener('click', function () { lbGo(lbIndex - 1); });
      if (lbNext) lbNext.addEventListener('click', function () { lbGo(lbIndex + 1); });
      lbTrack.addEventListener('scroll', debounce(function () { lbIndex = Math.round(lbTrack.scrollLeft / lbTrack.clientWidth); }, 60), { passive: true });
      lightbox.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') lbGo(lbIndex + 1);
        if (e.key === 'ArrowLeft') lbGo(lbIndex - 1);
      });
      lightbox.addEventListener('close', function () {
        document.body.classList.remove('is-locked');
        goTo(lbIndex, true);
      });
    }

    return { goTo: goTo };
  }

  function refreshCartCount() {
    fetch((S27.routes.cart || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  /* ---------- 8. Wishlist (saved in this browser) ---------- */
  var WISHLIST_KEY = 's27-wishlist';

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; }
  }
  function saveWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
  }

  function syncWishlist(scope) {
    var list = getWishlist();
    $$('[data-wishlist-toggle]', scope).forEach(function (btn) {
      var on = list.indexOf(btn.getAttribute('data-handle')) > -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var title = btn.getAttribute('aria-label').split(': ').slice(1).join(': ');
      btn.setAttribute('aria-label', (on ? strings.wishlistRemove : strings.wishlistAdd) + ': ' + title);
    });
    $$('[data-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  }

  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-handle');
      var list = getWishlist();
      var i = list.indexOf(handle);
      if (i > -1) { list.splice(i, 1); } else { list.unshift(handle); }
      saveWishlist(list);
      syncWishlist();
      showToast(i > -1 ? strings.wishlistRemoved : strings.wishlistSaved);
      var page = $('[data-wishlist-page]');
      if (page && i > -1) {
        var card = btn.closest('.product-grid__item');
        if (card) card.remove();
        if (!getWishlist().length) $('[data-wishlist-empty]', page).hidden = false;
      }
    });
    syncWishlist();
    renderWishlistPage();
  }

  function renderWishlistPage() {
    var page = $('[data-wishlist-page]');
    if (!page) return;
    var grid = $('[data-wishlist-grid]', page);
    var empty = $('[data-wishlist-empty]', page);
    var list = getWishlist();
    if (!list.length) { empty.hidden = false; return; }

    var root = S27.routes.root || '/';
    Promise.all(list.map(function (handle) {
      return fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var valid = products.filter(Boolean);
      // Remove products that no longer exist
      saveWishlist(valid.map(function (p) { return p.handle; }));
      if (!valid.length) { empty.hidden = false; syncWishlist(); return; }
      grid.innerHTML = valid.map(cardHtml).join('');
      syncWishlist(grid);
    });
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function cardHtml(p) {
    var img1 = p.images && p.images[0];
    var img2 = p.images && p.images[1];
    var price = (p.price_varies ? '<span class="price__from">From</span> ' : '') +
      '<span class="price__current">' + escapeHtml(formatMoney(p.price_min || p.price)) + '</span>';
    return '<li class="product-grid__item"><article class="card">' +
      '<div class="card__media"><a href="' + escapeHtml(p.url) + '" class="card__link" tabindex="-1" aria-hidden="true">' +
      (img1 ? '<img class="card__img card__img--primary" src="' + escapeHtml(sizedImage(img1, 720)) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
      (img2 ? '<img class="card__img card__img--secondary" src="' + escapeHtml(sizedImage(img2, 720)) + '" alt="" loading="lazy">' : '') +
      '</a>' +
      '<button type="button" class="wishlist-toggle card__wishlist" data-wishlist-toggle data-handle="' + escapeHtml(p.handle) + '" aria-pressed="true" aria-label="' + escapeHtml(strings.wishlistRemove) + ': ' + escapeHtml(p.title) + '">' +
      '<svg class="icon icon-heart" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"><path d="M12 20s-7.5-4.6-9.2-9.3C1.7 7.4 4 4.5 7.1 4.5c2 0 3.5 1.1 4.9 3 1.4-1.9 2.9-3 4.9-3 3.1 0 5.4 2.9 4.3 6.2C19.5 15.4 12 20 12 20z"/></svg>' +
      '</button></div>' +
      '<div class="card__info"><h3 class="card__title"><a href="' + escapeHtml(p.url) + '" class="card__title-link">' + escapeHtml(p.title) + '</a></h3>' +
      (p.type ? '<p class="card__type">' + escapeHtml(p.type) + '</p>' : '') +
      '<div class="price">' + price + '</div></div>' +
      '</article></li>';
  }

  /* ---------- 9. Bag (cart) page ---------- */
  function initCart() {
    var form = $('[data-cart-form]');
    if (!form) return;

    var submitSoon = debounce(function () { form.submit(); }, 600);

    // Quantity − / + buttons; the form saves itself shortly after a change
    $$('[data-quantity]', form).forEach(function (wrap) {
      var input = $('[data-quantity-input]', wrap);
      $('[data-quantity-minus]', wrap).addEventListener('click', function () {
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        submitSoon();
      });
      $('[data-quantity-plus]', wrap).addEventListener('click', function () {
        input.value = (parseInt(input.value, 10) || 0) + 1;
        submitSoon();
      });
      input.addEventListener('change', submitSoon);
    });

    // Save the order note as the customer types (it travels with the order to checkout)
    var note = $('[data-cart-note]', form);
    if (note) {
      note.addEventListener('input', debounce(function () {
        fetch((S27.routes.cart || '/cart') + '/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(function () {});
      }, 500));
    }
  }

  /* ---------- 10. Collection page ---------- */
  function initCollection() {
    var filterForm = $('[data-filter-form]');
    if (filterForm) {
      var sort = $('[data-sort-select]', filterForm);
      if (sort) sort.addEventListener('change', function () { filterForm.submit(); });
      // Remove empty price fields so the URL stays clean
      filterForm.addEventListener('submit', function () {
        $$('input[type="number"]', filterForm).forEach(function (i) { if (i.value === '') i.disabled = true; });
      });
    }
    initLoadMore();
  }

  function initLoadMore() {
    var wrap = $('[data-load-more]');
    var grid = $('[data-product-grid]');
    if (!wrap || !grid) return;
    var button = $('[data-load-more-button]', wrap);
    var loading = false;

    function load() {
      if (loading || !button) return;
      loading = true;
      wrap.classList.add('is-loading');
      fetch(button.href)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-product-grid]');
          var newWrap = doc.querySelector('[data-load-more]');
          if (newGrid) {
            $$('.product-grid__item', newGrid).forEach(function (item) { grid.appendChild(item); });
            initReveal(grid);
            syncWishlist(grid);
          }
          if (newWrap) {
            wrap.innerHTML = newWrap.innerHTML;
            button = $('[data-load-more-button]', wrap);
          } else {
            wrap.remove();
            button = null;
            if (observer) observer.disconnect();
          }
        })
        .catch(function () { window.location.href = button.href; })
        .finally(function () { loading = false; wrap.classList.remove('is-loading'); });
    }

    wrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-load-more-button]')) { e.preventDefault(); load(); }
    });

    var observer = null;
    if (wrap.hasAttribute('data-infinite') && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) load();
      }, { rootMargin: '600px 0px' });
      observer.observe(wrap);
    }
  }

  /* ---------- 11. Recommendations + extras ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(function (section) {
      var url = section.getAttribute('data-url');
      if (!url) return;
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            section.innerHTML = fresh.innerHTML;
            initCarousels(section);
            syncWishlist(section);
          }
        })
        .catch(function () {});
    });
  }

  function initAccountForms() {
    var recover = $('#RecoverPassword');
    var login = $('#CustomerLogin');
    if (!recover || !login) return;
    if (window.location.hash === '#recover') { recover.hidden = false; login.hidden = true; }
    $$('[data-toggle-recover]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        recover.hidden = !recover.hidden;
        login.hidden = !recover.hidden ? true : false;
      });
    });
  }

  /* ---------- Start ---------- */
  function init() {
    initHeader();
    initDrawer();
    initAnnouncement();
    initReveal();
    initCarousels();
    initVideos();
    initModals();
    initProduct();
    initWishlist();
    initCart();
    initCollection();
    initRecommendations();
    initAccountForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when sections are edited in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initCarousels(e.target);
    initVideos(e.target);
    syncWishlist(e.target);
    initHeader();
  });
})();
